import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getPackAuditLogs,
  getAllAuditLogs,
  getKnowledgePack,
  formatDateTime,
  exportAuditLogsToCSV,
} from '../api/knowledgePacks';

/**
 * NEXUS V6.3 Phase 2B - Audit Log Viewer
 * Filter and export audit history
 */
const KnowledgePackAuditLog = () => {
  const navigate = useNavigate();
  const { packId } = useParams();
  const { user } = useAuth();

  const [pack, setPack] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    action: 'all',
    limit: 50,
  });

  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management' || user?.role === 'management_functional';
  const isSupervisor = user?.role === 'supervisor';
  const canViewAudit = isAdmin || isManagement || isSupervisor;

  useEffect(() => {
    loadData();
  }, [packId, filters]);

  const loadData = async () => {
    if (!canViewAudit) {
      setError('You do not have permission to view audit logs');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (packId) {
        // Load specific pack audit logs
        const [packResponse, auditResponse] = await Promise.all([
          getKnowledgePack(packId, user?.role),
          getPackAuditLogs(packId, { limit: filters.limit }),
        ]);
        setPack(packResponse.pack);
        setLogs(auditResponse.audit_logs || []);
      } else {
        // Load global audit logs
        const params = {
          user_role: user?.role,
          limit: filters.limit,
        };
        if (filters.action !== 'all') {
          params.action = filters.action;
        }
        const response = await getAllAuditLogs(params);
        setLogs(response.audit_logs || []);
      }
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (logs.length === 0) {
      alert('No logs to export');
      return;
    }
    exportAuditLogsToCSV(logs);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white" style={{ height: '100dvh' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white px-4 py-3 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-lg">
              📋
            </div>
            <div>
              <h1 className="text-lg font-black leading-tight">Audit Logs</h1>
              <p className="text-xs font-medium opacity-90 truncate">
                {packId ? (pack?.name || 'Loading...') : 'All Packs'}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {(isAdmin || isManagement) && (
              <span className="text-[10px] font-medium px-2 py-0.5 bg-white/20 rounded">
                🟢 ISO Compliance Active
              </span>
            )}
            <button
              onClick={() => navigate('/knowledge-packs')}
              className="text-xs font-semibold px-3 py-1 bg-white/10 hover:bg-white/20 rounded transition"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 pb-20 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 64px)' }}>
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-gray-900">
              {logs.length} Log{logs.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={handleExport}
              className="px-3 py-1.5 bg-green-500 text-white text-xs font-semibold rounded-lg hover:bg-green-600 transition"
            >
              📥 Export CSV
            </button>
          </div>

          <div className="flex gap-2">
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="flex-1 px-2 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Actions</option>
              <option value="created">Created</option>
              <option value="updated">Updated</option>
              <option value="deleted">Deleted</option>
              <option value="version_created">Version Created</option>
              <option value="version_published">Published</option>
              <option value="version_rolled_back">Rolled Back</option>
            </select>

            <button
              onClick={loadData}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200 transition"
            >
              🔄
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="text-4xl mb-2">⏳</div>
            <p className="text-sm font-medium text-gray-600">Loading audit logs...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-3">
            <p className="text-sm font-semibold text-red-600">⚠️ {error}</p>
            <button onClick={loadData} className="mt-2 text-xs font-semibold text-red-600 underline">
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && logs.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="text-5xl mb-3">📋</div>
            <p className="text-base font-bold text-gray-800 mb-1">No Audit Logs</p>
            <p className="text-xs text-gray-600">No activity recorded yet</p>
          </div>
        )}

        {/* Logs List */}
        {!loading && !error && logs.length > 0 && (
          <div className="space-y-2">
            {logs.map(log => (
              <AuditLogCard key={log.log_id} log={log} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Audit Log Card Component
const AuditLogCard = ({ log }) => {
  const [expanded, setExpanded] = useState(false);

  const getActionColor = (action) => {
    const colors = {
      created: 'bg-blue-100 text-blue-700',
      updated: 'bg-yellow-100 text-yellow-700',
      deleted: 'bg-red-100 text-red-700',
      version_created: 'bg-purple-100 text-purple-700',
      version_published: 'bg-green-100 text-green-700',
      version_rolled_back: 'bg-orange-100 text-orange-700',
    };
    return colors[action] || 'bg-gray-100 text-gray-700';
  };

  const getActionIcon = (action) => {
    const icons = {
      created: '✨',
      updated: '✏️',
      deleted: '🗑️',
      version_created: '📝',
      version_published: '✓',
      version_rolled_back: '⏪',
    };
    return icons[action] || '📋';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-lg flex-shrink-0">{getActionIcon(log.action)}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${getActionColor(log.action)}`}>
                {log.action.toUpperCase().replace(/_/g, ' ')}
              </span>
            </div>
            <p className="text-xs text-gray-600 mt-0.5">
              {formatDateTime(log.timestamp)}
            </p>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-gray-500 hover:text-gray-700 text-sm font-bold"
        >
          {expanded ? '▼' : '▶'}
        </button>
      </div>

      {/* Summary */}
      <div className="space-y-1">
        {log.performed_by && (
          <div className="text-xs">
            <span className="font-semibold text-gray-700">By:</span>{' '}
            <span className="text-gray-600">
              {typeof log.performed_by === 'string' ? log.performed_by : JSON.stringify(log.performed_by)}
            </span>
          </div>
        )}
        {log.reason && (
          <div className="text-xs">
            <span className="font-semibold text-gray-700">Reason:</span>{' '}
            <span className="text-gray-600">
              {typeof log.reason === 'string' ? log.reason : typeof log.reason === 'object' && log.reason !== null && (log.reason.en || log.reason.ms) ? (log.reason.en || log.reason.ms) : JSON.stringify(log.reason)}
            </span>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
          {log.pack_id && (
            <div className="text-xs">
              <span className="font-semibold text-gray-700">Pack ID:</span>{' '}
              <span className="text-gray-600 font-mono text-[10px]">{log.pack_id}</span>
            </div>
          )}
          {log.changes && Object.keys(log.changes).length > 0 && (
            <div className="text-xs">
              <span className="font-semibold text-gray-700">Changes:</span>
              <pre className="mt-1 p-2 bg-gray-50 rounded text-[10px] overflow-x-auto">
                {JSON.stringify(log.changes, null, 2)}
              </pre>
            </div>
          )}
          {log.iso_hash && (
            <div className="text-xs">
              <span className="font-semibold text-gray-700">ISO Hash:</span>{' '}
              <span className="text-gray-600 font-mono text-[10px] break-all">{log.iso_hash}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KnowledgePackAuditLog;